import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Button as PolarisButton,
  Card,
  Badge as PolarisBadge,
  Text,
} from "@shopify/polaris";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import {
  fetchMyTrainingAssignments,
  TrainingAssignment,
} from "services/work-security-training.service";

function tone(status: string): any {
  if (status === "passed") return "success";
  if (status === "failed") return "critical";
  if (status === "in_progress") return "attention";
  return undefined;
}

function statusLabel(t: any, status: string) {
  switch (status) {
    case "passed":
      return t("test_passed", { defaultValue: "Passed" });
    case "failed":
      return t("test_failed", { defaultValue: "Failed" });
    case "in_progress":
      return t("training_status_in_progress", {
        defaultValue: "In progress",
      });
    case "pending":
      return t("training_status_pending", { defaultValue: "Pending" });
    default:
      return status;
  }
}

/** `hideHeading` drops the panel's own title for a page that already names the block. */
export default function MyTrainingPanel({ hideHeading = false }: { hideHeading?: boolean }) {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchMyTrainingAssignments(companyId);
      setAssignments(data.assignments || []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("load_failed", { defaultValue: "Failed to load training" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCount = useMemo(
    () =>
      assignments.filter(
        (a) => a.status !== "passed" && a.test?.status === "active"
      ).length,
    [assignments]
  );

  const columns: Column[] = [
    {
      key: "testName",
      name: t("name", { defaultValue: "Name" }),
      type: "custom",
      mobileView: "topLeft",
      // The phone card shows only four slots, so the duration and pass mark ride along under the name there.
      render: (item: any) => (
        <div>
          <div>{item.testName}</div>
          <div className="text-xs text-zinc-500 md:hidden">
            {item.durationLabel} · {item.passLabel}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      name: t("status", { defaultValue: "Status" }),
      type: "custom",
      mobileView: "topRight",
      render: (item: any) => (
        <PolarisBadge tone={tone(item.status)}>
          {statusLabel(t, item.status)}
        </PolarisBadge>
      ),
    },
    {
      key: "durationLabel",
      name: t("duration", { defaultValue: "Duration" }),
    },
    {
      key: "passLabel",
      name: t("pass_percent", { defaultValue: "Pass %" }),
    },
    {
      key: "attemptsCount",
      name: t("attempts", { defaultValue: "Attempts" }),
    },
    {
      key: "actions",
      name: t("actions", { defaultValue: "Actions" }),
      type: "custom",
      mobileView: "bottomLeft",
      render: (item: any) => (
        <div className="flex flex-wrap gap-2 justify-end max-md:justify-start">
          {item.openAttempt ? (
            <PolarisButton
              size="slim"
              variant="primary"
              onClick={() =>
                navigate(`/app/${companyId}/my-work/training/${item.id}?step=test`)
              }
            >
              {t("continue_test", { defaultValue: "Continue test" })}
            </PolarisButton>
          ) : null}
          {!item.openAttempt && item.status !== "passed" ? (
            <PolarisButton
              size="slim"
              variant="primary"
              onClick={() =>
                navigate(
                  `/app/${companyId}/my-work/training/${item.id}?step=${
                    item.trainingCompleted ? "test" : "training"
                  }`
                )
              }
            >
              {item.trainingCompleted
                ? t("open_test", { defaultValue: "Open" })
                : t("start_training", { defaultValue: "Start training" })}
            </PolarisButton>
          ) : null}
          {(item.attemptsCount || 0) > 0 ? (
            <PolarisButton
              size="slim"
              onClick={() =>
                navigate(
                  `/app/${companyId}/my-work/training/history/${item.id}`
                )
              }
            >
              {t("view_history", { defaultValue: "History" })}
            </PolarisButton>
          ) : null}
        </div>
      ),
    },
  ];

  const collection = assignments.map((a) => ({
    ...a,
    testName: a.test?.name || "—",
    durationLabel: `${a.test?.durationMinutes ?? "—"} ${t("min", {
      defaultValue: "min",
    })}`,
    passLabel: `${a.test?.passPercent ?? "—"}%`,
  }));

  return (
    <div className="mt-4 space-y-4">
      {error && (
        <Banner tone="critical" onDismiss={() => setError("")}>
          {error}
        </Banner>
      )}

      <Card>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            {!hideHeading && (
              <Text as="h2" variant="headingMd">
                {t("training_test", { defaultValue: "Training test" })}
              </Text>
            )}
            <Text as="p" variant="bodySm" tone="subdued">
              {openCount > 0
                ? t("training_open_count", {
                    defaultValue: "{{count}} test(s) still required",
                    count: openCount,
                  })
                : t("training_all_clear", {
                    defaultValue: "You are up to date on assigned tests.",
                  })}
            </Text>
          </div>
          <PolarisButton onClick={load}>
            {t("refresh", { defaultValue: "Refresh" })}
          </PolarisButton>
        </div>
      </Card>

      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
        {loading ? (
          <DataTableSkeleton rows={6} columns={columns.length} />
        ) : assignments.length === 0 ? (
          <div className="text-center py-16 px-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {t("no_training_assigned", {
                defaultValue: "No training tests assigned to you yet.",
              })}
            </h3>
          </div>
        ) : (
          <DataTable collection={collection} columns={columns} />
        )}
      </div>
    </div>
  );
}
